import React, { useState, useEffect } from "react";
import {
  Col,
  Button,
  Row,
  Spinner,
  Card,
  Modal,
  Form,
  Accordion
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faSms } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from "axios";
import { toast } from "react-toastify";
import { useAppContext } from 'providers/appProvider';
import { setDynamicFavicon } from '../../../common/favicon';
import { generateGUID } from '../../../../assets/js/common';
import { useParams } from "react-router-dom";
import { faClipboardCheck, faBuilding } from "@fortawesome/free-solid-svg-icons";
import imageCompression from 'browser-image-compression';

const LOGO_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.3,
  maxWidthOrHeight: 800,
  useWebWorker: true,
};

const LOGIN_IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

const UploadModal = ({ show, onHide, type, onUpload }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const schema = yup.object({
    uploadedFiles: yup
      .array()
      .min(1, t("pleaseUploadImage"))
      .test("fileSize", t("maxUploadSizeExceeded"), (files) => {
        // return files?.every(file => file.size <= 60 * 1024); // 20KB
        return files?.every(file => file.size <= 1.5 * 1024 * 1024); //
      })
      .required(t("fieldRequiredText"))
  });

  const {
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': ['.jpeg', '.png', '.jpg'] },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      if (acceptedFiles?.length) {
        const enrichedFiles = acceptedFiles.map(file =>
          Object.assign(file, { preview: URL.createObjectURL(file) })
        );
        setFiles(enrichedFiles);
        setValue("uploadedFiles", enrichedFiles, { shouldValidate: true });
        clearErrors("uploadedFiles");
      }
    }
  });

  const handleRemove = (path) => {
    const updated = files.filter(f => f.path !== path);
    setFiles(updated);
    setValue("uploadedFiles", updated.length ? updated : null, { shouldValidate: true });
  };

  const handleCancel = () => {
    setFiles([]);
    reset();            // Clears react-hook-form state
    onHide();           // Closes modal
  };
  const onSubmit = async () => {
    if (files.length) {
      setSaving(true); // Start loader
      let file = files[0];
      const extension = file.name.split('.').pop().toLowerCase();
      const originalSizeKB = (file.size / 1024).toFixed(1);

      const options = type === "logo" ? LOGO_COMPRESSION_OPTIONS : LOGIN_IMAGE_COMPRESSION_OPTIONS;

      if (file.size > options.maxSizeMB * 1024 * 1024) {
        try {
          const compressed = await imageCompression(file, options);
          file = new File([compressed], file.name, {
            type: compressed.type,
            lastModified: Date.now(),
          });
          console.log(
            `[${type}] compressed ${originalSizeKB} KB -> ${(file.size / 1024).toFixed(1)} KB`
          );
        } catch (err) {
          console.error('Image compression failed, uploading original file:', err);
        }
      } else {
        console.log(`[${type}] ${originalSizeKB} KB already under target, skipping compression`);
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result.split(',')[1];
        console.log(
          `[${type}] base64 payload being saved: ${(base64Image.length / 1024).toFixed(1)} KB`
        );
        onUpload(type, base64Image, extension, () => {
          setFiles([]);
          reset();
          setSaving(false); // Stop loader before closing
          onHide();
        });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header>
        <Modal.Title>{t("uploadImage")}</Modal.Title>
        <Button variant="close" onClick={handleCancel} />
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>

          <div className="mb-2 d-flex justify-content-center position-relative">
            {files.map(file => (
              <div key={file.path} className="text-center">
                {/* Delete Button at Top Right */}
                <Button
                  variant="link"
                  className="text-danger position-absolute"
                  style={{ top: 0, right: '2px', zIndex: 2 }}
                  onClick={() => handleRemove(file.path)}
                >
                  <FontAwesomeIcon icon="trash" />
                </Button>

                {/* Image Preview (Smaller) */}
                <img
                  src={file.preview}
                  alt="Preview"
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "contain",
                    borderRadius: "8px"
                  }}
                />

                {/* File Size */}
                <small className="text-muted d-block mt-1">
                  {(file.size / 1024).toFixed(1)} KB
                </small>
              </div>
            ))}
          </div>

          <div
            {...getRootProps({
              className: `dropzone-area py-4 text-center border border-secondary-subtle rounded ${errors.uploadedFiles ? 'is-invalid' : ''}`
            })}
          >
            <input {...getInputProps()} />
            <div>
              <img src={cloudUpload} alt="" width={25} className="me-2" />
              <p className="fs-9 mb-0 text-700">{t("dropYourImageHere")}</p>
            </div>
          </div>

          {errors.uploadedFiles && (
            <p className="text-danger mt-2">{errors.uploadedFiles.message}</p>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleCancel} disabled={saving} size="sm">
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)} disabled={files.length === 0 || saving} size="sm">
          {saving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {t("saving")}
            </>
          ) : (
            t("save")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const GeneralSettings = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [logoImage, setLogoImage] = useState(null);
  const [loginImage, setLoginImage] = useState(null);
  const [logoImageExtension, setLogoImageExtension] = useState(null);
  const [loginImageExtension, setLoginImageExtension] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [errors, setErrors] = useState({ logoImage: false, loginPageImage: false });
  const { refreshLogo } = useAppContext();

  const customerId = localStorage.getItem("customerId");

  useEffect(() => {
    const fetchSettings = async () => {
      const domainToUse = process.env.REACT_APP_DEFAULT_DOMAIN;

      const logoCacheKey = `cachedLogo_${customerId}`;     // Cache key by CustomerId
      const loginCacheKey = `cachedLoginBg_${domainToUse}`;  // Cache key by Domain

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/settingsRoute/getSettingsByCustomer/${customerId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );

        const {
          LogoImage,
          LogoImgExtension,
          LoginPageImage,
          LoginPageImgExtension,
          OrganizationId,
        } = res.data || {};

        // Remove old cache
        localStorage.removeItem(logoCacheKey);
        localStorage.removeItem(loginCacheKey);

        // Cache logo by CustomerId
        if (LogoImage && LogoImgExtension) {
          const logoData = `data:image/${LogoImgExtension};base64,${LogoImage}`;
          localStorage.setItem(logoCacheKey, logoData);
          setLogoImage(LogoImage);
          setLogoImageExtension(LogoImgExtension);
        }

        // Cache login background by domain
        if (LoginPageImage && LoginPageImgExtension) {
          const loginData = `data:image/${LoginPageImgExtension};base64,${LoginPageImage}`;
          localStorage.setItem(loginCacheKey, loginData);
          setLoginImage(LoginPageImage);
          setLoginImageExtension(LoginPageImgExtension);
        }

        if (OrganizationId) {
          setOrganizationId(OrganizationId);
        }

      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error(t("uploadImageFailed"), { position: "top-right", autoClose: 3000, toastId: "general-settings-fetch-error" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [customerId]);

  const handleUpload = async (type, base64Image, extension, onSuccessCloseModal) => {
    if (type === "logo") {
      setLogoImage(base64Image);
      setLogoImageExtension(extension);
    }
    if (type === "loginImage") {
      setLoginImage(base64Image);
      setLoginImageExtension(extension);
    }

    const customerId = localStorage.getItem("customerId");
    if (!customerId) {
      console.error("CustomerId is missing.");
      return;
    }

    const payload = {
      OrganizationId: organizationId || generateGUID(),
      CustomerId: customerId,
      LogoImage: type === "logo" ? base64Image : logoImage,
      LoginPageImage: type === "loginImage" ? base64Image : loginImage,
      LogoImgExtension: type === "logo" ? extension : logoImageExtension,
      LoginPageImgExtension: type === "loginImage" ? extension : loginImageExtension
    };

    setErrors((prev) => ({
      ...prev,
      [type === "logo" ? "logoImage" : "loginPageImage"]: false
    }));

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/settingsRoute/settings/save`,
        payload, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      if (res.status === 200) {
        setLogoImage(res.data.LogoImage);
        setLoginImage(res.data.LoginPageImage);
        setLogoImageExtension(res.data.LogoImgExtension);
        setLoginImageExtension(res.data.LoginPageImgExtension);

        // Clear session cache using domain from env
        const domainToUse = process.env.REACT_APP_DEFAULT_DOMAIN;
        localStorage.removeItem(`cachedLogo_${customerId}`);          // Logo cache key by CustomerId
        localStorage.removeItem(`cachedLoginBg_${domainToUse}`);    // Login background cache key by domain
        localStorage.removeItem(`genSettingsUpdated_${domainToUse}`);

        if (type === "logo") {
          refreshLogo();
          setDynamicFavicon();  // Update logo component only
        }

        toast.success(
          type === "logo"
            ? t("uploadLogoSuccess")
            : t("uploadLoginPageImageSuccess"),
          { position: "top-right", autoClose: 3000 }
        );

        if (typeof onSuccessCloseModal === 'function') {
          onSuccessCloseModal();
        }
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      toast.error(t("uploadImageFailed"), { position: "top-right", autoClose: 3000 });
    }
  };

  return isLoading ? (
    <div className="text-center my-5">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">{t("loading")}</span>
      </Spinner>
    </div>
  ) : (
    <>
      {/* Logo Row */}
      <Accordion alwaysOpen defaultActiveKey="0" className="mb-3">
        <Accordion.Item eventKey="0" className="mb-0">
          <Accordion.Header>
            <div className="w-100">
              <h6 className="fw-semi-bold ls fs-9 data-font-colour mb-0">
                {t("companyBranding")}
              </h6>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <Row className="mb-3 g-4">
              <Col lg={6}>
                <Card className={`w-100 h-100 ${!logoImage ? "card-missing-data" : "card-hover"}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="data-font-size data-font-colour">{t("uploadLogo")}</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-600"
                        onClick={() => setShowLogoModal(true)}
                      >
                        <FontAwesomeIcon icon="pencil-alt" />
                      </Button>
                    </div>
                    <div className="pt-3 text-center">
                      {logoImage ? (
                        <img
                          src={`data:image/${logoImageExtension};base64,${logoImage}`}
                          alt="Logo"
                          style={{ maxHeight: 100 }}
                        />
                      ) : (
                        <p className="pt-2" style={{ fontStyle: "italic", color: "#666" }}></p>
                      )}
                    </div>
                    {errors.logoImage && (
                      <div className="text-danger text-center mt-2">
                        {t("fieldRequiredText")}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className={`w-100 h-100 ${!loginImage ? "card-missing-data" : "card-hover"}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="data-font-size data-font-colour">{t("uploadLoginPageImage")}</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-600"
                        onClick={() => setShowLoginModal(true)}
                      >
                        <FontAwesomeIcon icon="pencil-alt" />
                      </Button>
                    </div>
                    <div className="pt-3 text-center">
                      {loginImage ? (
                        <img
                          src={`data:image/${loginImageExtension};base64,${loginImage}`}
                          alt="Login Page"
                          style={{ maxHeight: 100 }}
                        />
                      ) : (
                        <p className="pt-2" style={{ fontStyle: "italic", color: "#666" }}></p>
                      )}
                    </div>
                    {errors.loginPageImage && (
                      <div className="text-danger text-center mt-2">
                        {t("fieldRequiredText")}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <UploadModal
        show={showLogoModal}
        onHide={() => setShowLogoModal(false)}
        type="logo"
        onUpload={handleUpload}
      />
      <UploadModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        type="loginImage"
        onUpload={handleUpload}
      />
    </>
  );
};

export default GeneralSettings;